import { useEffect, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { api, ApiError } from '../../lib/api.js';
import { useApi } from '../../hooks/useApi.js';
import { useAuth } from '../../hooks/useAuth.jsx';
import { Button } from '../../components/Button.jsx';
import { TagInput } from './components/TagInput.jsx';
import { ShapeTabs } from './components/ShapeTabs.jsx';
import { WeightTabs } from './components/WeightTabs.jsx';
import { DensityGrid } from './components/DensityGrid.jsx';

const INK = '#F5EEDC';
const PRINT_TYPES = ['SP', 'RP', 'CBW SP'];
const STEP_PRESETS = {
  standard: ['100','95','90','80','70','60','50','40','30','20','10','5','3','1'],
  extended: ['100','95','90','80','70','60','50','40','30','20','10','5','3','1','0.8','0.4'],
};
const DEFAULT_INKS = [
  { kind: 'cyan',    name: 'C' },
  { kind: 'magenta', name: 'M' },
  { kind: 'yellow',  name: 'Y' },
  { kind: 'black',   name: 'K' },
];
// Computed at call time (not module load) so a tab left open across
// midnight still defaults new readings to the actual current date.
const isoToday = () => new Date().toISOString().slice(0, 10);

const blankReading = ({ customerId = null, customerProfileId = null, profile = null } = {}) => ({
  customerId, customerProfileId,
  jobNumber: '', jobName: '', setNumber: '',
  tags: [], printType: profile?.printSide || '', readingDate: isoToday(),
  stepPreset: 'standard', notes: '',
  inks: DEFAULT_INKS,
  stepLabels: STEP_PRESETS.standard,
  shapes: [],
});

// Sample data fills realistic readings — for testing report export
const SAMPLE = () => ({
  jobNumber: '141023', jobName: 'Sample Q2 colour audit', setNumber: '01',
  tags: ['CRS', 'XPS'], printType: 'RP', readingDate: isoToday(),
  stepPreset: 'standard',
  inks: DEFAULT_INKS,
  stepLabels: STEP_PRESETS.standard,
  shapes: [
    {
      dotType: 'CRS', dotNumber: '501',
      weights: [
        {
          lpi: '150#',
          density: [1.55, 1.75, 1.35, 1.85],
          steps: [
            [100, 100, 100, 100],   // 100 — will be overwritten in render anyway
            [96.4, 98.1, 97.8, 98.5],
            [94.2, 95.0, 94.7, 95.4],
            [89.1, 90.2, 89.6, 90.4],
            [83.0, 84.5, 83.7, 84.6],
            [76.8, 78.4, 77.5, 78.0],
            [69.1, 71.4, 70.8, 71.7],
            [59.0, 62.0, 60.5, 61.8],
            [47.5, 50.1, 49.0, 49.8],
            [34.2, 37.0, 35.8, 36.4],
            [20.1, 22.5, 21.4, 22.0],
            [10.5, 12.8, 11.5, 12.1],
            [6.4, 7.9, 7.0, 7.6],
            [2.1, 3.0, 2.5, 2.8],
          ],
        },
      ],
    },
  ],
});

export const InkDensityReadingPage = ({ id }) => {
  const { route } = useLocation();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isNew = !id || id === 'new';
  const url = new URL(window.location.href);
  const seedCustomer = isNew ? Number(url.searchParams.get('customer')) || null : null;
  const seedProfile  = isNew ? Number(url.searchParams.get('profile'))  || null : null;

  const [reading, setReading] = useState(() => blankReading({ customerId: seedCustomer, customerProfileId: seedProfile }));
  const [activeShape, setActiveShape] = useState(0);
  const [activeWeight, setActiveWeight] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const { data: existing, loading, error: loadError } = useApi(isNew ? null : `/ink-density/${id}`);
  const { data: customers, refetch: refetchCustomers } = useApi('/customers');
  const { data: seedProfileData } = useApi(seedProfile ? `/customer-profiles/${seedProfile}` : null);
  // Profiles owned by the currently-selected customer
  const { data: profilesForCustomer } = useApi(
    (reading.customerId) ? `/customer-profiles?customer=${reading.customerId}` : null,
  );

  const addCustomer = async () => {
    const name = window.prompt('New customer name:')?.trim();
    if (!name) return;
    try {
      const c = await api.post('/customers', { name });
      await refetchCustomers();
      setReading((r) => ({ ...r, customerId: c.id, customerProfileId: null }));
    } catch (e) { alert(e.message ?? 'Failed'); }
  };

  useEffect(() => {
    if (!isNew && existing) setReading(existing);
  }, [isNew, existing]);

  useEffect(() => {
    if (isNew && seedProfileData) {
      setReading((r) => ({
        ...r,
        customerId: seedProfileData.customerId ?? r.customerId,
        customerProfileId: seedProfileData.id,
        printType: seedProfileData.printSide || r.printType,
      }));
    }
  }, [isNew, seedProfileData]);

  const updateField = (k, v) => setReading((r) => ({ ...r, [k]: v }));

  const onPresetChange = (preset) => {
    const labels = STEP_PRESETS[preset] || reading.stepLabels;
    setReading((r) => ({
      ...r,
      stepPreset: preset,
      stepLabels: preset === 'custom' ? r.stepLabels : labels,
      shapes: r.shapes.map((s) => ({
        ...s,
        weights: s.weights.map((w) => ({
          ...w,
          steps: w.steps.slice(0, labels.length),
        })),
      })),
    }));
  };

  const fillSample = () => {
    const s = SAMPLE();
    setReading((r) => ({ ...r, ...s, customerId: r.customerId, customerProfileId: r.customerProfileId }));
    setActiveShape(0); setActiveWeight(0);
  };

  const addShape = (shape) => {
    setReading((r) => ({ ...r, shapes: [...r.shapes, { ...shape, weights: shape.weights ?? [] }] }));
    setActiveShape(reading.shapes.length);
    setActiveWeight(0);
  };
  const removeShape = (i) => {
    setReading((r) => ({ ...r, shapes: r.shapes.filter((_, k) => k !== i) }));
    setActiveShape((cur) => Math.max(0, cur > i ? cur - 1 : (cur === i ? 0 : cur)));
  };
  const updateShape = (i, patch) => {
    setReading((r) => ({ ...r, shapes: r.shapes.map((s, k) => k === i ? { ...s, ...patch } : s) }));
  };
  const addWeight = (w) => {
    if (!reading.shapes[activeShape]) return;
    updateShape(activeShape, { weights: [...reading.shapes[activeShape].weights, w] });
    setActiveWeight(reading.shapes[activeShape].weights.length);
  };
  const removeWeight = (i) => {
    if (!reading.shapes[activeShape]) return;
    const ws = reading.shapes[activeShape].weights.filter((_, k) => k !== i);
    updateShape(activeShape, { weights: ws });
    setActiveWeight((cur) => Math.max(0, cur > i ? cur - 1 : (cur === i ? 0 : cur)));
  };
  const updateWeight = (i, w) => {
    if (!reading.shapes[activeShape]) return;
    updateShape(activeShape, { weights: reading.shapes[activeShape].weights.map((x, k) => k === i ? w : x) });
  };

  const save = async () => {
    if (!reading.customerId) { setError(new Error('Pick a customer first.')); return; }
    setBusy(true); setError(null);
    try {
      const payload = {
        ...reading,
        jobNumber: reading.jobNumber?.trim() || null,
        jobName: reading.jobName?.trim() || null,
        setNumber: reading.setNumber?.trim() || null,
        notes: reading.notes?.trim() || null,
        readingDate: reading.readingDate || null,
        printType: reading.printType || null,
      };
      const saved = isNew
        ? await api.post('/ink-density', payload)
        : await api.patch(`/ink-density/${id}`, payload);
      if (isNew) route(`/ink-density/${saved.id}`);
      else setReading(saved);
    } catch (e) { setError(e); }
    finally { setBusy(false); }
  };

  const renderReport = async () => {
    if (isNew) {
      // Save first — saved state will route; user can re-click. Simpler: bail.
      await save();
      return;
    }
    const win = window.open('', '_blank');
    if (!win) { setError(new Error('Popup blocked — allow popups for this site to view the report.')); return; }
    try {
      const res = await fetch(`/api/v1/ink-density/${id}/report`, { method: 'POST', credentials: 'include' });
      if (!res.ok) { win.close(); setError(new Error(`Failed to generate report (${res.status})`)); return; }
      const html = await res.text();
      win.document.open(); win.document.write(html); win.document.close();
    } catch (e) {
      win.close();
      setError(e);
    }
  };

  const remove = async () => {
    if (!window.confirm('Delete this reading?')) return;
    try {
      await api.delete(`/ink-density/${id}`);
      route('/ink-density');
    } catch (e) {
      setError(e);
    }
  };

  if (loading) return <p class="text-sm text-text-tertiary">Loading…</p>;
  if (loadError) return <p class="text-sm font-semibold text-ink-magenta">Failed to load: {loadError.message}</p>;

  const shape = reading.shapes[activeShape];
  const weight = shape?.weights?.[activeWeight];

  const heading = [
    existing?.customerName ?? (customers ?? []).find((c) => c.id === reading.customerId)?.name,
    [...(reading.tags || []), reading.printType].filter(Boolean).join(' '),
  ].filter(Boolean).join(' — ') || (isNew ? 'New density reading' : 'Density reading');

  return (
    <div>
      <nav class="mb-2 text-[11px] text-text-tertiary">
        <a href="/ink-density" class="text-text-tertiary hover:text-text-primary">Ink density</a> · {isNew ? 'New reading' : (reading.jobNumber || `#${id}`)}
      </nav>

      <section class="mb-4 flex items-start justify-between gap-4">
        <div>
          <div class="mb-1.5 flex items-center gap-2">
            <span class="block h-2.5 w-2.5 rounded-full" style={{ background: INK, border: '1px solid rgba(245,238,220,0.4)' }} />
            <span class="text-[11px] font-semibold uppercase tracking-eyebrow" style={{ color: INK }}>Ink density</span>
          </div>
          <h1 class="m-0 font-serif text-[22px] font-medium text-text-primary">{heading}</h1>
          {existing?.profileLabel && (
            <p class="m-0 mt-1 text-[12px] text-text-secondary">From profile: {existing.profileLabel}</p>
          )}
        </div>
        <div class="flex gap-2">
          {isAdmin && <Button variant="ghost" onClick={fillSample} title="Fill realistic sample data for testing"><i class="ti ti-flask" /> Sample data</Button>}
          {!isNew && <Button variant="ghost" onClick={renderReport}>Render report</Button>}
          {!isNew && isAdmin && <Button variant="ghost" onClick={remove} class="text-ink-magenta">Delete</Button>}
          <Button variant="primary" onClick={save} disabled={busy || !reading.customerId}>{busy ? 'Saving…' : 'Save'}</Button>
        </div>
      </section>

      <details open class="mb-4 rounded-md border border-border-subtle bg-bg-card p-3">
        <summary class="cursor-pointer text-[12px] text-text-secondary">Reading metadata</summary>

        <div class="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
          <Field label="Customer">
            <div class="flex gap-1">
              <select value={reading.customerId ?? ''} onChange={(e) => updateField('customerId', Number(e.currentTarget.value) || null)} class={`${selectClass} flex-1`}>
                <option value="">— pick customer —</option>
                {(customers ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <button type="button" onClick={addCustomer} class="rounded-md border border-border-strong px-2 py-1 text-[11px] text-text-tertiary hover:text-text-primary" title="Create new customer">+</button>
            </div>
          </Field>
          <Field label="Profile (optional)">
            <select value={reading.customerProfileId ?? ''} onChange={(e) => updateField('customerProfileId', Number(e.currentTarget.value) || null)} class={selectClass}>
              <option value="">— ad-hoc, no profile —</option>
              {(profilesForCustomer ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {[p.pressType, p.substrate, p.printSide].filter(Boolean).join(' · ') || p.profileName}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Job #"><input value={reading.jobNumber} onInput={(e) => updateField('jobNumber', e.currentTarget.value)} class={inputClass} placeholder="141023" /></Field>
          <Field label="Job name"><input value={reading.jobName} onInput={(e) => updateField('jobName', e.currentTarget.value)} class={inputClass} /></Field>
          <Field label="Set #"><input value={reading.setNumber} onInput={(e) => updateField('setNumber', e.currentTarget.value)} class={inputClass} /></Field>
          <Field label="Reading date"><input type="date" value={reading.readingDate ?? ''} onInput={(e) => updateField('readingDate', e.currentTarget.value)} class={inputClass} /></Field>
          <Field label="Print type">
            <select value={reading.printType ?? ''} onChange={(e) => updateField('printType', e.currentTarget.value)} class={selectClass}>
              <option value="">—</option>
              {PRINT_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </Field>
          <Field label="Step preset">
            <select value={reading.stepPreset} onChange={(e) => onPresetChange(e.currentTarget.value)} class={selectClass}>
              <option value="standard">Standard (14)</option>
              <option value="extended">Extended (16)</option>
              <option value="custom">Custom</option>
            </select>
          </Field>
        </div>

        <div class="mt-3">
          <p class="mb-1 text-[10px] font-semibold uppercase tracking-eyebrow text-text-tertiary">Tags</p>
          <TagInput value={reading.tags ?? []} onChange={(t) => updateField('tags', t)} />
        </div>

        <div class="mt-3">
          <p class="mb-1 text-[10px] font-semibold uppercase tracking-eyebrow text-text-tertiary">Notes</p>
          <textarea rows={2} value={reading.notes ?? ''} onInput={(e) => updateField('notes', e.currentTarget.value)} class={`${inputClass} resize-none`} />
        </div>
      </details>

      <ShapeTabs
        shapes={reading.shapes}
        activeIdx={activeShape}
        onSelect={(i) => { setActiveShape(i); setActiveWeight(0); }}
        onAdd={addShape}
        onRemove={removeShape}
      />

      {shape ? (
        <div class="rounded-b-md bg-bg-card p-4">
          <div class="mb-3">
            <WeightTabs
              weights={shape.weights}
              activeIdx={activeWeight}
              onSelect={setActiveWeight}
              onAdd={addWeight}
              onRemove={removeWeight}
            />
          </div>
          {weight ? (
            <DensityGrid
              weight={weight}
              inks={reading.inks}
              stepLabels={reading.stepLabels}
              onChange={(w) => updateWeight(activeWeight, w)}
            />
          ) : (
            <p class="py-6 text-center text-sm text-text-tertiary">Add an LPI weight to start entering densities.</p>
          )}
        </div>
      ) : (
        <div class="rounded-b-md bg-bg-card p-6 text-center">
          <p class="m-0 text-sm text-text-tertiary">Add a shape (e.g. CRS 501) to start.</p>
        </div>
      )}

      {error && (
        <p class="mt-3 text-[13px] font-semibold text-ink-magenta">
          {error instanceof ApiError ? error.message : (error.message ?? 'Failed')}
        </p>
      )}
    </div>
  );
};

const inputClass = 'block w-full rounded-md border border-border-strong bg-bg-card px-2.5 py-1.5 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand';
const selectClass = inputClass;
const Field = ({ label, children }) => (
  <div>
    <p class="mb-1 text-[10px] font-semibold uppercase tracking-eyebrow text-text-tertiary">{label}</p>
    {children}
  </div>
);
